import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import type { ReactNode } from 'react';
import type { CtaProps, SecondaryCtaProps, StackedCtaProps } from './cta.types';
import { CtaButton } from './cta-button';
import { CtaLink } from './cta-link';

/** The heading `PageShell` stamps, so a host can mirror the visible title elsewhere. */
const PAGE_TITLE_ID = 'page-shell-title';
type PageShellProps = {
  children: ReactNode;
  title?: string;
  subtitle?: ReactNode;
  header?: ReactNode;
  cta?: CtaProps;
  secondaryCta?: SecondaryCtaProps;
  /** Actions stacked in this order under `cta` and `secondaryCta`, each drawn by its own `variant`. */
  ctas?: StackedCtaProps[];
  /** Centered under the actions, or in their place when there are none. */
  caption?: ReactNode;
  isLoading?: boolean;
  headerIcon?: ReactNode;
  headerTitleClassName?: string;
  options?: {
    headerTitleCentered?: boolean;
  };
};
const SecondaryCta = t0 => {
  const $ = _c(2);
  const {
    cta
  } = t0;
  let t1;
  if ($[0] !== cta) {
    t1 = cta.variant === "link" ? <CtaLink cta={cta} /> : <CtaButton cta={cta} variant="secondary" />;
    $[0] = cta;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

// A stacked text link gets a row as tall as a button, for a full tap target. A `secondaryCta`
// link stays bare so the pages that use it don't move.
const StackedCta = t0 => {
  const $ = _c(7);
  const {
    cta,
    isLoading
  } = t0;
  if (cta.variant === "primary") {
    let t1;
    if ($[0] !== cta || $[1] !== isLoading) {
      t1 = <CtaButton cta={cta} isLoading={isLoading} />;
      $[0] = cta;
      $[1] = isLoading;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (cta.variant === "secondary") {
    let t1;
    if ($[3] !== cta) {
      t1 = <CtaButton cta={cta} variant="secondary" />;
      $[3] = cta;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] !== cta) {
    t1 = <div className="flex h-10 items-center justify-center"><CtaLink cta={cta} /></div>;
    $[5] = cta;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
const PageShell = t0 => {
  const $ = _c(22);
  const {
    children,
    title,
    subtitle,
    header,
    cta,
    secondaryCta,
    ctas: t1,
    caption,
    isLoading,
    headerIcon,
    options: t2
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const ctas = t3;
  let t4;
  let t5;
  if ($[2] !== header || $[3] !== headerIcon || $[4] !== subtitle || $[5] !== t2 || $[6] !== title) {
    const {
      headerTitleCentered: t6
    } = t2 === undefined ? {
      headerTitleCentered: false
    } : t2;
    const headerTitleCentered = t6 === undefined ? false : t6;
    t4 = "page-shell flex flex-col gap-6 flex-1";
    t5 = header ?? <header className="page-shell__header fp-custom-appearance fp-page-header flex flex-col gap-2 items-center">{headerIcon && <div className="flex justify-center items-center">{headerIcon}</div>}{title && <h1 id={PAGE_TITLE_ID} className={cx("fp-custom-appearance fp-page-header-title text-heading-3 text-primary", {
        "text-center": headerTitleCentered
      })}>{title}</h1>}{subtitle && <p className="fp-custom-appearance fp-page-header-subtitle text-body-2 text-secondary text-center">{subtitle}</p>}</header>;
    $[2] = header;
    $[3] = headerIcon;
    $[4] = subtitle;
    $[5] = t2;
    $[6] = title;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== children) {
    t6 = children && <main className="page-shell__content flex-1">{children}</main>;
    $[9] = children;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== caption || $[12] !== cta || $[13] !== ctas || $[14] !== isLoading || $[15] !== secondaryCta) {
    t7 = (cta || secondaryCta || ctas.length > 0 || caption) && <div className="page-shell__cta flex flex-col gap-3">{cta && <CtaButton cta={cta} isLoading={isLoading} />}{secondaryCta && <SecondaryCta cta={secondaryCta} />}{ctas.map((stacked, index) => <StackedCta key={index} cta={stacked} isLoading={isLoading} />)}{caption && <p className="page-shell__caption text-body-2 text-tertiary text-center">{caption}</p>}</div>;
    $[11] = caption;
    $[12] = cta;
    $[13] = ctas;
    $[14] = isLoading;
    $[15] = secondaryCta;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t8 = <div className={t4}>{t5}{t6}{t7}</div>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};
export type { CtaProps, PageShellProps, SecondaryCtaProps, StackedCtaProps };
export { PAGE_TITLE_ID };
export default PageShell;